import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { RatioArrow } from './shared';
import { formatKMBWithPlaceholder } from '@intelligence/v3/utils/number';

import type { ILineChartTooltipProps, ILineTooltipDataItem, ICreateLineChartTooltipFormatterParams } from '../types';

/**
 * 折线图 Tooltip 组件
 */
const LineChartTooltip: React.FC<ILineChartTooltipProps> = (props) => {
  const { dateLabel, metricName, dataList, showDataSourceIcon = false, dataSourceIcon } = props;

  // 按原始数值降序排列
  const sortedDataList = [...dataList].sort((a, b) => {
    const aIsOthers = (a?.name?.toLowerCase() || '') === 'others';
    const bIsOthers = (b?.name?.toLowerCase() || '') === 'others';

    if (aIsOthers && !bIsOthers) {
      return 1;
    }

    if (!aIsOthers && bIsOthers) {
      return -1;
    }

    const aValue = a.rawValue ?? Number.NEGATIVE_INFINITY;
    const bValue = b.rawValue ?? Number.NEGATIVE_INFINITY;
    return bValue - aValue;
  });

  return (
    <div className="v3-chart-tooltip v3-line-tooltip">
      <div className="v3-tooltip-table">
        <div className="v3-tooltip-cell v3-tooltip-cell--left">
          <div className="v3-tooltip-header-text">{dateLabel}</div>
        </div>
        <div className="v3-tooltip-cell v3-tooltip-cell--right">
          <div className="v3-tooltip-title-right">
            {showDataSourceIcon && dataSourceIcon && (
              <img
                className="v3-tooltip-datasource-icon"
                src={dataSourceIcon}
                alt="dataSource"
                width={14}
                height={14}
              />
            )}
            {metricName && <div className="v3-tooltip-title-metric">{metricName}</div>}
          </div>
        </div>
        {sortedDataList.map((item, index) => (
          <React.Fragment key={index}>
            <div className="v3-tooltip-cell v3-tooltip-cell--left">
              <div className="v3-tooltip-list-item-left">
                <span className="v3-tooltip-list-item-dot" style={{ backgroundColor: item.color }} />
                {item.iconUrl && (
                  <img className="v3-tooltip-list-item-icon" src={item.iconUrl} alt={item.iconAlt || item.name} />
                )}
                <span className="v3-tooltip-list-item-name">{item.name}</span>
              </div>
            </div>
            <div className="v3-tooltip-cell v3-tooltip-cell--right">
              <div className="v3-tooltip-list-item-right">
                <span className="v3-tooltip-list-item-value">{item.value}</span>
                {item.ratio && <RatioArrow type={item.ratio.type} value={item.ratio.value} />}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

/**
 * 渲染折线图 Tooltip 为 HTML 字符串
 */
export const renderLineChartTooltip = (props: ILineChartTooltipProps): string => {
  return renderToStaticMarkup(<LineChartTooltip {...props} />);
};

/**
 * 创建折线图 Tooltip formatter
 * @description 基于传入的 formatters 生成 ECharts tooltipFormatter 函数
 */
export const createLineChartTooltipFormatter = (
  params: ICreateLineChartTooltipFormatterParams,
): ((echartsParams: any) => string) => {
  const {
    metricName,
    seriesNameFormatter,
    valueFormatter = formatKMBWithPlaceholder,
    xAxisLabelFormatter,
    ratioFormatter,
    dataSourceIcon,
    showDataSourceIcon = false,
  } = params;

  return (echartsParams: any): string => {
    // ECharts axis trigger 返回数组
    const paramList = Array.isArray(echartsParams) ? echartsParams : [echartsParams];

    if (paramList.length === 0) {
      return '';
    }

    // 获取 X 轴值（时间点）
    const axisValue = paramList[0]?.axisValue || paramList[0]?.name || '';
    const dateLabel = xAxisLabelFormatter ? xAxisLabelFormatter(axisValue) : axisValue;

    // 构建数据列表
    const dataList: ILineTooltipDataItem[] = paramList.map((param: any) => {
      const rawValue = Array.isArray(param.value) ? param.value[1] : param.value;
      const displayName = seriesNameFormatter ? seriesNameFormatter(param.seriesName || '') : param.seriesName || '';

      // 获取环比值
      const ratioValue = param.data?.ratio ?? null;
      const ratio = ratioFormatter ? ratioFormatter(ratioValue) : undefined;
      const iconUrl = param.data?.payload?.iconUrl || param.data?.iconUrl;
      const iconAlt = param.data?.payload?.iconAlt || param.data?.iconAlt || displayName;

      return {
        color: param.color || '#999',
        name: displayName,
        value: valueFormatter(rawValue),
        rawValue: typeof rawValue === 'number' ? rawValue : null,
        iconUrl,
        iconAlt,
        ratio,
      };
    });

    return renderLineChartTooltip({
      dateLabel,
      metricName,
      dataList,
      dataSourceIcon,
      showDataSourceIcon,
    });
  };
};

export default LineChartTooltip;
